import type { ActionContext } from 'vuex'
import type { RootState } from '@/store'
import { CATEGORY, emptyConsent } from '@/constants/cookie-registry'
import { applyConsent } from '@/helpers/consent'
import { readConsentCategories, readConsentState, writeConsentState } from '@/helpers/consent/state'
import {
  computeOptimisticExpiry,
  currentBannerVersion,
  currentCookieListVersion,
  needsReConsent,
  shouldShowBanner,
} from '@/helpers/consent/policy'
import { buildDecision } from '@/helpers/consent/decision'
import { STORAGE_KEY, enqueue, flush, queueLength } from '@/helpers/consent/queue'
import { postConsent } from '@/helpers/consent/api'

type Categories = { [key: string]: boolean }
type Screen = 'hidden' | 'banner' | 'settings'
type Source = 'banner' | 'settings' | 'footer_link'
type ReConsentReason = 'expired' | 'list_changed' | null

interface State {
  categories: Categories
  draft: Categories
  consentId: string | null
  expiresAt: string | null
  screen: Screen
  openedFrom: 'banner' | 'footer'
  outboxSize: number
  // Why the banner is back: the consent ran out, or the cookie list changed under it. Only
  // INIT_CONSENT can tell - it drops expiresAt for a lapsed consent, so by the time a button
  // is pressed the difference is gone. Null on a first visit.
  reConsentReason: ReConsentReason
}

const state: State = {
  categories: emptyConsent(),
  draft: emptyConsent(),
  consentId: null,
  expiresAt: null,
  screen: 'hidden',
  openedFrom: 'banner',
  outboxSize: 0,
  reConsentReason: null,
}

const getters = {
  consentCategories: (state: State) => state.categories,
  consentDraft: (state: State) => state.draft,
  consentScreen: (state: State) => state.screen,
  isBannerVisible: (state: State) => state.screen === 'banner',
  isSettingsVisible: (state: State) => state.screen === 'settings',
  isConsentOpen: (state: State) => state.screen !== 'hidden',
  hasConsentDecision: (state: State) => Boolean(state.expiresAt),
}

const mutations = {
  SET_CONSENT_STATE(state: State, payload: Partial<State>) {
    Object.assign(state, payload)
  },
  SET_SCREEN(state: State, payload: { screen: Screen; openedFrom?: 'banner' | 'footer' }) {
    state.screen = payload.screen
    if (payload.openedFrom) state.openedFrom = payload.openedFrom
  },
  RESET_DRAFT(state: State) {
    state.draft = { ...state.categories }
  },
  SET_DRAFT_CATEGORY(state: State, payload: { key: string; value: boolean }) {
    state.draft = { ...state.draft, [payload.key]: payload.value }
  },
  SET_OUTBOX_SIZE(state: State, size: number) {
    state.outboxSize = size
  },
}

const sender = (item: any) => postConsent(item)

// Expiry wins when both are true: a lapsed consent is not valid at all, while a changed list
// only means the question is worth asking again.
function readReConsentReason(stored: any): ReConsentReason {
  if (!stored) return null
  if (stored.isExpired) return 'expired'
  if (needsReConsent(stored)) return 'list_changed'
  return null
}

const actions = {
  INIT_CONSENT: ({ commit }: ActionContext<State, RootState>) => {
    const stored = readConsentState()

    commit('SET_CONSENT_STATE', {
      categories: stored && !stored.isExpired ? stored.categories : emptyConsent(),
      consentId: stored ? stored.id : null,
      expiresAt: stored && !stored.isExpired ? stored.expiresAt : null,
      outboxSize: queueLength(),
      reConsentReason: readReConsentReason(stored),
    })
    commit('RESET_DRAFT')
    commit('SET_SCREEN', { screen: shouldShowBanner(stored) ? 'banner' : 'hidden' })
  },

  OPEN_SETTINGS: (
    { commit }: ActionContext<State, RootState>,
    payload: { openedFrom?: 'banner' | 'footer' } = {},
  ) => {
    commit('RESET_DRAFT')
    commit('SET_SCREEN', { screen: 'settings', openedFrom: payload.openedFrom || 'footer' })
  },

  CLOSE_SETTINGS: ({ commit, state, getters }: ActionContext<State, RootState>) => {
    const hasDecision = getters.hasConsentDecision
    commit('SET_SCREEN', {
      screen: hasDecision || state.openedFrom === 'footer' ? 'hidden' : 'banner',
    })
  },

  ACCEPT_ALL: ({ dispatch }: ActionContext<State, RootState>, payload: { source: Source }) =>
    dispatch('DECIDE', {
      action: 'accept_all',
      source: payload.source,
      categories: { ...emptyConsent(), [CATEGORY.ANALYTICS]: true, [CATEGORY.MARKETING]: true },
    }),

  // Always reject_all, never withdraw. The contract says to look at the control that was
  // used, not at what was stored before: withdraw belongs to a separate "withdraw consent"
  // button, and there is none - the banner and the settings screen both offer "reject all".
  // It also means the front does not have to know whether consent existed, which it cannot
  // know anyway once the cookie is gone.
  REJECT_ALL: ({ dispatch }: ActionContext<State, RootState>, payload: { source: Source }) =>
    dispatch('DECIDE', {
      action: 'reject_all',
      source: payload.source,
      categories: emptyConsent(),
    }),

  SAVE_CUSTOM: ({ dispatch, state }: ActionContext<State, RootState>, payload: { source: Source }) =>
    dispatch('DECIDE', {
      action: 'custom',
      source: payload.source,
      categories: { ...state.draft, [CATEGORY.TECHNICAL]: true },
    }),

  DECIDE: (
    { commit, state, dispatch, rootGetters }: ActionContext<State, RootState>,
    payload: { action: string; source: Source; categories: Categories },
  ) => {
    const categories = payload.categories
    const region = rootGetters['regions/currentRegion']
    const country = (region && region.alpha2Code) || null

    // The journal has to tell the settings screen apart from the footer link, so the
    // screen the decision was made on is not enough - openedFrom is what distinguishes them.
    const source: Source =
      payload.source === 'settings' && state.openedFrom === 'footer'
        ? 'footer_link'
        : payload.source

    // Consent Mode holds back the next firing, not the tag already running: a recorder or a
    // pixel the container loaded under the old consent keeps going until the page is gone.
    // Taking a category away is therefore a reload, and clearRevokedCookies() sweeps up after.
    const isRevoking = Object.keys(categories).some(
      (key) => state.categories[key] && !categories[key],
    )

    // Read once and used for both the body and the cookie: the journal and the cookie have to
    // name the same revision, and reading them separately would let a publication landing between
    // the two calls leave them disagreeing.
    const bannerVersion = currentBannerVersion()
    const cookieListVersion = currentCookieListVersion()

    const decision = buildDecision({
      action: payload.action,
      reConsentReason: state.reConsentReason,
      categories,
      source,
      consentId: state.consentId,
      country,
      bannerVersion,
      cookieListVersion,
    })

    applyConsent(categories, { dropQueues: true })

    const expiresAt = computeOptimisticExpiry()
    writeConsentState({
      id: state.consentId,
      expiresAt,
      categories,
      cookieListVersion,
      bannerVersion,
    })

    commit('SET_CONSENT_STATE', { categories, expiresAt, reConsentReason: null })
    commit('RESET_DRAFT')
    commit('SET_SCREEN', { screen: 'hidden' })

    enqueue(decision)
    commit('SET_OUTBOX_SIZE', queueLength())

    if (isRevoking) {
      // Send the decision before reloading. The queue would survive the reload, but a
      // visitor who never comes back would leave the journal without the record.
      const reload = () => window.location.reload()
      return dispatch('FLUSH_OUTBOX').then(reload, reload)
    }

    return dispatch('FLUSH_OUTBOX')
  },

  FLUSH_OUTBOX: ({ commit, state }: ActionContext<State, RootState>) => {
    return flush(sender, (result: any, item: any) => {
      if (!result || (!result.consentId && !result.expiresAt)) return

      const consentId = result.consentId || state.consentId
      const expiresAt = result.expiresAt || state.expiresAt

      // The versions come off the decision that was just accepted, not off whatever is current.
      // An item queued in an earlier page load - or before a revision was published - was
      // journalled under its own versions, and the cookie has to say the same, otherwise the
      // next needsReConsent() compares against a revision this visitor never saw.
      const body = (item && item.body) || {}

      writeConsentState({
        id: consentId,
        expiresAt,
        categories: state.categories,
        cookieListVersion: body.cookie_list_version || null,
        bannerVersion: body.banner_version || null,
      })
      commit('SET_CONSENT_STATE', { consentId, expiresAt })
    }).then(() => {
      commit('SET_OUTBOX_SIZE', queueLength())
    })
  },
}

// A decision in another tab writes a cookie, and a cookie change raises no event anywhere -
// this listener fires only because every decision also touches the outbox in localStorage.
export function wireConsentCrossTab(store: any) {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return

    store.dispatch('consent/INIT_CONSENT')

    // INIT_CONSENT brings the store in line, and that alone would hide the banner while the
    // tags in this tab kept running under the old consent: Consent Mode was never told, and
    // the cookies of a category that has just been taken away are still sitting here.
    // Deliberately no reload to match what DECIDE does in the deciding tab - a reload here
    // would take unsaved forms in an untouched tab with it.
    applyConsent(readConsentCategories())
  })
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
